"use client";

import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { BackendLabel } from "./backend-label";
import {
  backendSelectContentClass,
  backendSelectItemClass,
  backendSelectTriggerClass,
} from "./backend-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

export interface BackendSelectProps {
  // Additional props
  options: { value: string; label: string }[];
  error?: boolean | string;
  helperText?: string;
  containerClassName?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  className?: string;
  onChange?: (value: string) => void;
  size?: "sm" | "md";
}

export interface BackendSelectWithLabelProps extends BackendSelectProps {
  label?: string;
  labelClassName?: string;
  required?: boolean;
}

export interface BackendControlledSelectProps<
  TFieldValues extends FieldValues = FieldValues,
> extends Omit<BackendSelectWithLabelProps, "onChange" | "value" | "name"> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
}

const BackendSelect = forwardRef<HTMLDivElement, BackendSelectProps>(
  (
    {
      className,
      options,
      error = false,
      helperText,
      containerClassName,
      placeholder,
      onChange,
      value,
      defaultValue,
      disabled,
      readOnly,
      name,
      id,
      size = "md",
      ...props
    },
    ref
  ) => {
    const isSmall = size === "sm";

    // Check if className contains height override (like ControlledSelect does)
    const hasHeightOverride =
      className?.includes("h-") || className?.includes("h-[");

    const triggerStyles = cn(
      backendSelectTriggerClass,
      isSmall
        ? "h-[34px] px-3 py-2 text-sm"
        : "h-10 px-3 py-2 text-[16px] lg:text-sm",
      // Override SelectTrigger's data-attribute height (data-[size=default]:h-9) with !important
      // Same approach as ControlledSelect uses
      hasHeightOverride && "!h-auto",
      // Only apply default height if className doesn't override it
      !hasHeightOverride && !isSmall && "h-[42px]",
      // Force the card background to win over Radix SelectTrigger's bg-background
      // (which references an undefined var in the backend theme → transparent).
      !readOnly && "!bg-[var(--c-card)]",
      readOnly && "!cursor-default !bg-[var(--c-surface-sunken)] select-text",
      error && "border-destructive focus:border-destructive",
      className
    );

    // Determine if there's an error (either boolean true or non-empty string)
    const hasError = Boolean(error);

    // If error is a string, use it as helperText
    const displayHelperText = typeof error === "string" ? error : helperText;

    const handleValueChange = (selectedValue: string) => {
      // Prevent selection in read-only mode
      if (readOnly) return;
      if (onChange) {
        onChange(selectedValue);
      }
    };

    return (
      <div className="flex w-full flex-col" ref={ref}>
        <div className={cn("relative text-sm", containerClassName)}>
          <Select
            value={value === "" ? undefined : value}
            defaultValue={defaultValue === "" ? undefined : defaultValue}
            onValueChange={handleValueChange}
            disabled={disabled}
            name={name}
            {...props}
          >
            <SelectTrigger className={triggerStyles} id={id}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className={backendSelectContentClass}>
              {options
                .filter((option) => option.value !== "") // Filter out empty string values
                .map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={readOnly}
                    className={backendSelectItemClass}
                  >
                    {option.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {/* Absolut positionierte Fehlermeldung */}
          {displayHelperText && (
            <p
              className={cn(
                "absolute -bottom-5 left-1 text-sm",
                hasError ? "text-destructive" : "text-[var(--c-muted)]"
              )}
            >
              {displayHelperText}
            </p>
          )}
        </div>
      </div>
    );
  }
);

const BackendSelectWithLabel = forwardRef<
  HTMLDivElement,
  BackendSelectWithLabelProps
>(({ className, label, labelClassName, id, required, ...props }, ref) => {
  return (
    <div className="flex flex-col gap-1 sm:gap-1.5">
      {label && (
        <BackendLabel
          htmlFor={id}
          required={required}
          className={labelClassName}
        >
          {label}
        </BackendLabel>
      )}
      <BackendSelect id={id} className={className} ref={ref} {...props} />
    </div>
  );
});

// New controlled select component for react-hook-form
const BackendControlledSelect = <
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  ...props
}: BackendControlledSelectProps<TFieldValues>) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <BackendSelectWithLabel {...props} {...field} error={error?.message} />
  );
};

BackendSelect.displayName = "BackendSelect";
BackendSelectWithLabel.displayName = "BackendSelectWithLabel";
BackendControlledSelect.displayName = "BackendControlledSelect";

export { BackendSelect, BackendSelectWithLabel, BackendControlledSelect };
